import React from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import styles from './Notifications.module.scss';
import { Notification } from '../types/notification.types';
import { fetchUserNotifications, markNotificationAsRead } from '../api/notificationsApi';
import { mapAPINotificationsToNotifications } from '../utils/notificationMappers';

interface NotificationsProps {
	isOpen: boolean;
	onClose: () => void;
}

const Notifications: React.FC<NotificationsProps> = ({ isOpen, onClose }) => {
	const history = useHistory();
	const queryClient = useQueryClient();
	const [markingAsRead, setMarkingAsRead] = React.useState<string | null>(null);
	const [markingAllAsRead, setMarkingAllAsRead] = React.useState(false);
	const [removingNotifications, setRemovingNotifications] = React.useState<Set<string>>(new Set());

	// Fetch notifications from API
	const { data: apiNotifications = [], isLoading } = useQuery(
		'userNotifications',
		fetchUserNotifications,
		{
			refetchInterval: 30000, // Refetch every 30 seconds
			staleTime: 10000,
		}
	);

	const notifications = mapAPINotificationsToNotifications(apiNotifications);
	// Filter out notifications that are currently being removed
	// console.log("all notifications", notifications);
	const displayedNotifications = notifications.filter(
		(n) => !removingNotifications.has(n.id) && !n.isRead
	);
	// console.log("displayed notifications", displayedNotifications, removingNotifications);
	const unreadCount = displayedNotifications.filter((n) => !n.isRead).length;

	// Mutation for marking notification as read
	const markAsReadMutation = useMutation(markNotificationAsRead, {
		onSuccess: () => {
			queryClient.invalidateQueries('userNotifications');
		},
	});

	const markAsRead = async (id: string) => {
		setMarkingAsRead(id);

		try {
			// Call API to mark as read
			await markNotificationAsRead(id);

			// Add to removing set to hide it from the list
			setRemovingNotifications((prev) => new Set(prev).add(id));

			queryClient.invalidateQueries('userNotifications');
			setMarkingAsRead(null);
		} catch (error) {
			// If API fails, reset the loading state
			setMarkingAsRead(null);
		}
	};

	const markAllAsRead = async () => {
		setMarkingAllAsRead(true);
		const unreadNotifications = displayedNotifications.filter((n) => !n.isRead);

		try {
			// Call API for all notifications in parallel
			await Promise.all(
				unreadNotifications.map((notification) => 
					markNotificationAsRead(notification.id)
				)
			);

			// Add all to removing set to hide them
			setRemovingNotifications(new Set(unreadNotifications.map((n) => n.id)));

			// Start animation and invalidate query
			setTimeout(() => {
				queryClient.invalidateQueries('userNotifications');
				setMarkingAllAsRead(false);

				// Clear from removing set after the query refetches
				setTimeout(() => {
					setRemovingNotifications(new Set());
				}, 1000);

				// Close the dialog after clearing all notifications
				onClose();
			}, 300);
		} catch (error) {
			// If API fails, reset the loading state
			setMarkingAllAsRead(false);
		}
	};

	const handleNotificationClick = (notification: Notification) => {
		if (!notification.isRead) {
			markAsRead(notification.id);
		}
		if (notification.actionUrl) {
			history.push(notification.actionUrl);
			onClose();
		}
	};

	const formatTimestamp = (timestamp: Date): string => {
		const now = new Date();
		const diff = now.getTime() - timestamp.getTime();
		const minutes = Math.floor(diff / (1000 * 60));
		const hours = Math.floor(diff / (1000 * 60 * 60));
		const days = Math.floor(diff / (1000 * 60 * 60 * 24));

		if (minutes < 1) return 'Just now';
		if (minutes < 60) return `${minutes}m ago`;
		if (hours < 24) return `${hours}h ago`;
		if (days === 1) return 'Yesterday';
		if (days < 7) return `${days}d ago`;
		return timestamp.toLocaleDateString();
	};

	if (!isOpen) return null;

	return (
		<>
			<div className={styles.notificationsOverlay} onClick={onClose} />
			<div className={styles.notificationsContainer}>
				<div className={styles.notificationsHeader}>
					<div className={styles.headerLeft}>
						<h3>Notifications</h3>
						{unreadCount > 0 && <span className={styles.unreadBadge}>{unreadCount}</span>}
					</div>
					{unreadCount > 0 && (
						<button
							className={styles.markAllButton}
							onClick={markAllAsRead}
							disabled={markingAllAsRead}
						>
							{markingAllAsRead ? (
								<span className={styles.buttonContent}>
									<span className={styles.spinner} />
									Clearing...
								</span>
							) : (
								'Clear all'
							)}
						</button>
					)}
				</div>

				<div className={styles.notificationsList}>
					{isLoading ? (
						<div className={styles.emptyState}>
							<p>Loading notifications...</p>
						</div>
					) : displayedNotifications.filter((n) => !n.isRead).length === 0 ? (
						<div className={styles.emptyState}>
							<p>No notifications</p>
						</div>
					) : (
						displayedNotifications
							.filter((n) => !n.isRead)
							.map((notification) => (
								<div
									key={notification.id}
									className={`${styles.notificationItem} ${styles.unread} ${styles[notification.type]} ${
										removingNotifications.has(notification.id) ? styles.removing : ''
									}`}
									onClick={() => handleNotificationClick(notification)}
								>
									<div className={styles.notificationContent}>
										<div className={styles.notificationHeader}>
											<h4>{notification.title}</h4>
											<span className={styles.timestamp}>
												{formatTimestamp(notification.timestamp)}
											</span>
										</div>
										<p className={styles.notificationMessage}>{notification.message}</p>
									</div>
									<button
										className={styles.markReadButton}
										onClick={(e) => {
											e.stopPropagation();
											markAsRead(notification.id);
										}}
										disabled={markingAsRead === notification.id}
									>
										{markingAsRead === notification.id ? (
											<span className={styles.buttonContent}>
												<span className={styles.spinner} />
											</span>
										) : (
											'Clear'
										)}
									</button>
								</div>
							))
					)}
				</div>
			</div>
		</>
	);
};

export default Notifications;
